import React from 'react'
import { useFindMenu, useTrans, useTealium } from '@next-nx/hooks'
import { ArrowLeft, Email, Phone, Print, Time } from '@next-nx/shared-ui/icons'
import {
  WContainer,
  Breadcrumbs,
  MainTitle,
  Button,
  StorelocatorMarker,
  CustomLdJson,
} from '@next-nx/shared-ui/atoms'
import { Page, StoreLocatorHelpBanner, StorelocatorMap } from '@next-nx/shared-ui/organisms'
import { getTimestamp } from '@next-nx/utils'
import styles from './FilialeTemplate.module.sass'
import { FilialeType } from '@next-nx/utils/types'

interface Props {
  filiale: FilialeType
  pagina: any
}

const FilialeTemplate = (props: Props) => {
  const { filiale, pagina = {} } = props

  const t = useTrans()

  const contatti = [
    ...(filiale.telefono
      ? [{ icon: <Phone />, value: <a href={`tel:${filiale.telefono}`}>{filiale.telefono}</a> }]
      : []),
    ...(filiale.fax
      ? [{ icon: <Print />, value: <a href={`fax:${filiale.fax}`}>{filiale.fax}</a> }]
      : []),
    ...(filiale.email
      ? [{ icon: <Email />, value: <a href={`mailto:${filiale.email}`}>{filiale.email}</a> }]
      : []),
  ]

  const helpBannerMenu = useFindMenu('help_banner')
  const helpBannerStoreLocatorMenu = useFindMenu('store-locator', helpBannerMenu)
  const { linkDataLayer } = useTealium()

  const handleTracking = (action_type?: string | number, info: string = '') => {
    const action_timestamp = getTimestamp()

    if (typeof action_type === 'number') {
      action_type =
        action_type === 0
          ? 'chiama'
          : action_type === 1
          ? 'stampa'
          : action_type === 2
          ? 'invio_email'
          : undefined
    }

    linkDataLayer({
      virtual: 'action',
      action_name: 'storelocator',
      action_tech: 'click',
      action_type: `filiali_${action_type}`,
      action_timestamp,
      action_effect: 'filiali_interazione',
      cta: 'pagina_dettaglio',
      info: info?.replace(/ /g, '_').replace(/,/g, '').toLowerCase(),
    })
  }

  return (
    <Page pagina={pagina} className={`${styles.root}`}>
      <WContainer>
        <Breadcrumbs breadcrumbs={pagina.breadcrumbs} className={styles.breadcrumbs} />
        <MainTitle
          label={t(`Filiale UnipolRental a {0}`, [filiale.titolo || ''])}
          className={styles.title}
        />
        <div className={styles.backBox}>
          <Button
            label={t('Torna alla ricerca')}
            icon={<ArrowLeft />}
            href="/filiali"
            variant="ghost"
          />
        </div>
        <div className={styles.layout}>
          <div className={styles.content}>
            <div className={styles.item}>
              <p className={styles.itemTitle}>{t('Tipologia')}</p>
              <p className={styles.itemContent}>{t('Filiale')}</p>
            </div>
            <div className={styles.item}>
              <p className={styles.itemTitle}>{t('Indirizzo')}</p>
              <div className={styles.itemContent}>
                {!!filiale.indirizzo && <>{filiale.indirizzo},</>}
                {filiale.cap} {filiale.comune}
                {!!filiale.provincia && <> ({filiale.provincia})</>}
              </div>
            </div>
            {contatti.length > 0 && (
              <div className={`${styles.item} ${styles['item--icons']}`}>
                <p className={styles.itemTitle}>{t('Contatti')}</p>
                <div className={styles.itemContent}>
                  {contatti.map((x, index) => (
                    <div
                      className={styles.itemRow}
                      key={index}
                      onClick={() => handleTracking(index, filiale.titolo)}
                    >
                      <span className={styles.itemRowIcon}>{x.icon}</span>
                      <span
                        className={`${styles.itemRowValue} ${styles['itemRowValue--underline']}`}
                      >
                        {x.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!!filiale.orari && (
              <div className={`${styles.item} ${styles['item--icons']}`}>
                <p className={styles.itemTitle}>{t('Orari di apertura')}</p>
                <div className={styles.itemContent}>
                  <div className={styles.itemRow}>
                    <span className={styles.itemRowIcon}>
                      <Time />
                    </span>
                    <span className={styles.itemRowValue}>{filiale.orari}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.mapWrap}>
            <div className={styles.map}>
              <StorelocatorMap
                loaderClassName={styles.loader}
                mapContainerClassName={styles.mapContainer}
                showMarkers={true}
                mapCoords={{
                  lat: parseFloat(filiale.latitudine || ''),
                  lng: parseFloat(filiale.longitudine || ''),
                }}
                zoom={17}
              >
                <StorelocatorMarker type={'filiali'} item={filiale} selected={false} />
              </StorelocatorMap>
            </div>
            {!!(filiale.email || filiale.telefono) && (
              <div className={styles.actions}>
                {!!filiale.telefono && (
                  <div className={styles.actionsItem}>
                    <Button
                      icon={<Phone />}
                      href={`tel:${filiale.telefono}`}
                      onClick={() => handleTracking('chiama', filiale.titolo)}
                    />
                    <Button
                      label={t('Chiama')}
                      variant="ghost"
                      href={`tel:${filiale.telefono}`}
                      onClick={() => handleTracking('chiama', filiale.titolo)}
                    />
                  </div>
                )}
                {!!filiale.email && (
                  <div className={styles.actionsItem}>
                    <Button
                      icon={<Email />}
                      href={`mailto:${filiale.email}`}
                      onClick={() => handleTracking('invio_email', filiale.titolo)}
                    />
                    <Button
                      label={t('Manda e-mail')}
                      variant="ghost"
                      href={`mailto:${filiale.email}`}
                      onClick={() => handleTracking('invio_email', filiale.titolo)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </WContainer>
      {helpBannerStoreLocatorMenu && (
        <StoreLocatorHelpBanner item={helpBannerStoreLocatorMenu} className={styles.banner} />
      )}
      <CustomLdJson
        type="Car"
        objectId={filiale.pk}
        object={{
          '@context': 'https://schema.org/',
          '@type': 'AutomotiveBusiness',
          'name': `Filiale UnipolRental a ${filiale.titolo}`,
          'openingHours': filiale.orari || '',
          'address': {
            '@type': 'PostalAddress',
            'addressLocality': filiale.comune || '',
            'addressRegion': filiale.provincia || '',
            'postalCode': filiale.cap || '',
            'streetAddress': filiale.indirizzo || '',
          },
          'longitude': filiale.longitudine || '',
          'latitude': filiale.latitudine || '',
          'email': filiale.email || '',
          'telephone': filiale.telefono || '',
          'faxNumber': filiale.fax || '',
        }}
      />
    </Page>
  )
}

export default FilialeTemplate
